import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingUp, Users, Rocket, Sparkles } from "lucide-react";
import { STATS } from "@/lib/constants";

const iconMap = { TrendingUp, Users, Rocket, Sparkles };

const useCountUp = (target, active, duration = 1400) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
};

const StatCard = ({ stat, active }) => {
  const Icon = iconMap[stat.icon] || Sparkles;
  const value = useCountUp(stat.value, active);
  const rounded = stat.decimals ? value.toFixed(stat.decimals) : Math.floor(value).toLocaleString("en-IN");
  return (
    <div className="glass rounded-2xl p-6 sm:p-7 text-center transition-all glass-hover">
      <div className="w-11 h-11 mx-auto rounded-xl grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] mb-4">
        <Icon size={20} />
      </div>
      <div className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-white">
        {stat.prefix || ""}{rounded}{stat.suffix || ""}
      </div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mt-2">{stat.label}</div>
      <div className="text-xs text-white/50 mt-1.5">{stat.sub}</div>
    </div>
  );
};

export const Statistics = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  return (
    <section id="stats" data-testid="statistics-section" className="py-16 sm:py-20 relative" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
        >
          {STATS.map((s) => (
            <StatCard key={s.label} stat={s} active={inView} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
